import { ImpersonationSupabase, ServerSupabase, SuperAdminSupabase } from "#/lib/supabase/index";
import { isBrowser } from "@supabase/ssr";
import { getBrowserJWT, getBrowserUser } from "./utils";
import { Database } from "#/types/supabase";
import { createClient } from "@supabase/supabase-js";
import type { SupabaseClientOptions } from "@supabase/supabase-js";
import { createAccessToken } from "#/utils/jwt";


export const createSupabaseAdminServerClient = (
  options?: SupabaseClientOptions<"public">
): ServerSupabase => {

  // const env = process.env;
  if (isBrowser()) {
    throw new Error("This function should only be called on the server");
  }
  const serverSupabase = createClient<Database>(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.SUPABASE_SECRET_KEY!,
    {
      auth: {
        persistSession: false,
        detectSessionInUrl: false,
        ...(options?.auth ?? {})
      },
      ...(options ?? {})
    }
  ) as unknown as ServerSupabase;
  serverSupabase.type = "server";
  return serverSupabase;
};


const getServerStorage = (): SupabaseClientOptions<Database>["auth"] => {
  const storage: any = {};
  return {
    detectSessionInUrl: false,
    autoRefreshToken: false,
    storage: {
      getItem(key: string): string | null {
        if (storage[key] === undefined) {
          return null;
        }
        return storage[key];
      },
      setItem(key: string, value: string): void {
        storage[key] = value;
      },
      removeItem(key: string): void {
        delete storage[key];
      }
    }
  };
};

// let supabaseSuperUserServerClient: Promise<SuperAdminSupabase>;
/** Supabase client type, where it's a server client that's logged into a super_user account. This is
 *  needed because changes made by admin clients don't seem to trigger realtime updates
 */
export const createSupabaseSuperUserServerClient = async (): Promise<SuperAdminSupabase> => {
  if (isBrowser()) {
    throw new Error("This function should only be called on the server");
  }
  // const env = process.env;
  const serverSupabase = createClient(process.env.NEXT_PUBLIC_SUPABASE_URL!, process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!, {
    auth: getServerStorage()
  }) as unknown as SuperAdminSupabase;
  serverSupabase.type = "super_admin";

  await serverSupabase.auth.initialize();
  const signInResult = await serverSupabase.auth.signInWithPassword({
    email: process.env.SUPABASE_ADMIN_EMAIL as string,
    password: process.env.SUPABASE_ADMIN_PASSWORD as string
  });
  if (signInResult.error || !signInResult.data.user?.id) {
    throw signInResult.error ?? new Error("No super user found");
  }
  return serverSupabase;
};

type CreateSupabaseUserServerClientOptions =
  | {
    req: { cookies: Record<string, any> },
    res?: any,
    userId?: never
  }
  | {
    req?: never,
    res?: never,
    userId: string
  };

/** Initializes a client in a server function which impersonates the user making the request */
export const createSupabaseUserImpersonationServerClient = async ({
  req,
  userId: _userId
}: CreateSupabaseUserServerClientOptions): Promise<ImpersonationSupabase> => {
  if (isBrowser()) {
    throw new Error("This function should only be called on the server");
  }

  let accessToken: string;
  let userId: string;
  if (req) {
    const user = await getBrowserUser({
      req
    });
    if (!user?.id) {
      throw new Error("No user found");
    }
    userId = user.id;
    const JWT = await getBrowserJWT({
      req
    });
    if (!JWT) {
      throw new Error("No JWT found");
    }
    accessToken = JWT;
  } else {
    userId = _userId;
    accessToken = await createAccessToken(_userId);
  }

  // const env = process.env;
  const serverSupabase = createClient(process.env.NEXT_PUBLIC_SUPABASE_URL!, process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!, {
    auth: getServerStorage(),
    global: {
      headers: {
        "x-client-info": "supabase-ssr/0.5.2",
        apiKey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
        Authorization: `Bearer ${accessToken}`,
        "user-id": userId
      }
    }
  }) as unknown as ImpersonationSupabase;
  serverSupabase.type = "impersonation";

  return serverSupabase;
};